import React, { createContext, useState, useEffect, useMemo, useCallback } from "react";
import { clearPersonaSessionState } from "../utils/personaStorage";

const CART_KEY = "shoppingCart";
const COUPON_KEY = "shoppingCoupon";
const SHIPPING_KEY = "selectedShipping";
const SHIPPING_PERSONA_KEY = "selectedShippingPersonaId";
const SHIPPING_OPTIONS_KEY = "shippingOptions";
const CART_HASH_KEY = "cartHash";

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [maxInstallments, setMaxInstallments] = useState(12);
  const [cart, setCart] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState(() => {
    try {
      const savedPersonaId = localStorage.getItem(SHIPPING_PERSONA_KEY);
      const currentPersonaId = localStorage.getItem("selectedProfileId");
      if (
        savedPersonaId &&
        currentPersonaId &&
        String(savedPersonaId) !== String(currentPersonaId)
      ) {
        localStorage.removeItem(SHIPPING_KEY);
        localStorage.removeItem(SHIPPING_PERSONA_KEY);
        return null;
      }
      const savedShipping = localStorage.getItem(SHIPPING_KEY);
      return savedShipping ? JSON.parse(savedShipping) : null;
    } catch {
      return null;
    }
  });
  const [shippingOptions, setShippingOptions] = useState([]);
  const [lastCalculatedZip, setLastCalculatedZip] = useState(null);
  const [cartHash, setCartHash] = useState(null);
  const [userFilters, setUserFilters] = useState(null);
  const [coupon, setCoupon] = useState(() => {
    try {
      // Cupom legado corrompido: lista multi-cupom ("A,B,C") salva como código.
      // Código válido nunca tem vírgula; descarta nas duas chaves pra destravar o usuário.
      const isCorrupted = (raw) => {
        try {
          return String(JSON.parse(raw)?.code || "").includes(",");
        } catch {
          return false;
        }
      };
      ["coupon", COUPON_KEY].forEach((key) => {
        const raw = localStorage.getItem(key);
        if (raw && isCorrupted(raw)) localStorage.removeItem(key);
      });
      const savedCoupon = localStorage.getItem(COUPON_KEY);
      return savedCoupon ? JSON.parse(savedCoupon) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const storedCart = localStorage.getItem(CART_KEY);
    if (storedCart) setCart(JSON.parse(storedCart));
  }, []);

  useEffect(() => {
    if (cart && cart.length > 0) {
      localStorage.setItem(CART_KEY, JSON.stringify(cart));
    } else {
      localStorage.removeItem(CART_KEY);
    }
  }, [cart]);

  useEffect(() => {
    if (coupon) {
      localStorage.setItem(COUPON_KEY, JSON.stringify(coupon));
    } else {
      localStorage.removeItem(COUPON_KEY);
      localStorage.removeItem("coupon");
    }
  }, [coupon]);

  useEffect(() => {
    try {
      if (selectedShipping) {
        localStorage.setItem(SHIPPING_KEY, JSON.stringify(selectedShipping));
        const currentPersonaId = localStorage.getItem("selectedProfileId");
        if (currentPersonaId) {
          localStorage.setItem(SHIPPING_PERSONA_KEY, String(currentPersonaId));
        }
      } else {
        localStorage.removeItem(SHIPPING_KEY);
        localStorage.removeItem(SHIPPING_PERSONA_KEY);
      }
    } catch {}
  }, [selectedShipping]);

  // Calcular hash do carrinho para detectar mudanças
  useEffect(() => {
    const hash = JSON.stringify(cart.map(item => ({
      id: item.id,
      quantity: item.quantity,
      price: item.price,
      var_id: item.var_id
    })));
    setCartHash(hash);
  }, [cart]);

  const itemsCount = useMemo(
    () => cart.reduce((sum, item) => sum + (item.quantity || 0), 0),
    [cart]
  );

  const subtotal = useMemo(
    () =>
      cart.reduce((sum, item) => {
        const price = Number(item.price ?? 0);
        const qty = Number(item.quantity ?? 0);
        return sum + price * qty;
      }, 0),
    [cart]
  );

  const shippingValue = useMemo(
    () => Number(selectedShipping?.price ?? 0),
    [selectedShipping]
  );

  const discountValue = useMemo(() => {
    if (!coupon) return 0;
    const v = Number(coupon.value ?? 0);
    if (coupon.type === "percent") return (subtotal * v) / 100;
    if (coupon.type === "fixed") return v;
    return 0;
  }, [coupon, subtotal]);

  const total = useMemo(() => {
    const t = subtotal - discountValue + shippingValue;
    return t < 0 ? 0 : t;
  }, [subtotal, discountValue, shippingValue]);

  // addItemToCart com suporte a replace / merge
  const addItemToCart = useCallback(({ item, quantity = 1, replace = false, merge = false }) => {
    setCart((prev) => {
      const idx = prev.findIndex((ci) => ci.id === item.id);
      if (idx >= 0) {
        const current = prev[idx];
        const next = [...prev];
        if (replace) {
          next[idx] = {
            ...item,
            quantity: item.quantity ?? current.quantity ?? quantity,
          };
          return next;
        }
        if (merge) {
          next[idx] = {
            ...current,
            ...item,
            quantity: current.quantity + quantity,
          };
          return next;
        }
        next[idx] = { ...current, quantity: current.quantity + quantity };
        return next;
      }
      return [...prev, { ...item, quantity }];
    });
  }, []);

  // NOVO: updateItemInCart para sobrescrever/mesclar (sem alterar quantity se não vier)
  const updateItemInCart = useCallback((updated) => {
    console.log('updateItemInCart', updated);
    if (!updated || updated.id == null) return;
    setCart((prev) =>
      prev.map((item) =>
        item.id === updated.id
          ? {
              ...item,
              ...updated,
              quantity:
                updated.quantity != null ? updated.quantity : item.quantity,
            }
          : item
      )
    );
  }, []);

  const removeItemFromCart = useCallback(
    (itemId) => setCart((prev) => prev.filter((i) => i.id !== itemId)),
    [],
  );

  const resetShipping = useCallback(() => {
    setSelectedShipping(null);
    setShippingOptions([]);
    setLastCalculatedZip(null);
    try {
      localStorage.removeItem(SHIPPING_KEY);
      localStorage.removeItem(SHIPPING_PERSONA_KEY);
      localStorage.removeItem(SHIPPING_OPTIONS_KEY);
      localStorage.removeItem("lastCalculatedZip");
    } catch {}
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
    setCoupon(null);
    setSelectedShipping(null);
    setShippingOptions([]);
    setLastCalculatedZip(null);
    setCartHash(null);
    try {
      localStorage.removeItem(CART_KEY);
      localStorage.removeItem(COUPON_KEY);
      localStorage.removeItem("coupon");
      localStorage.removeItem(SHIPPING_KEY);
      localStorage.removeItem(SHIPPING_PERSONA_KEY);
      localStorage.removeItem(SHIPPING_OPTIONS_KEY);
      localStorage.removeItem(CART_HASH_KEY);
      localStorage.removeItem("kitFlavorPreference");
      localStorage.removeItem("kitRecorrencia");
      localStorage.removeItem("checkoutKitId");
      localStorage.removeItem("selectedFormulaIds");
    } catch {}
  }, []);

  // Limpa o carrinho, cupons e estado transitivo automaticamente quando a persona é alterada
  useEffect(() => {
    const handleProfileChanged = () => {
      clearCart();
      clearPersonaSessionState();
    };
    const handleStorageChange = (e) => {
      if (e.key === "selectedProfileId" && e.newValue !== e.oldValue) {
        clearCart();
        clearPersonaSessionState();
      }
    };
    window.addEventListener("profileChanged", handleProfileChanged);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("profileChanged", handleProfileChanged);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [clearCart]);

  return (
    <CartContext.Provider
      value={{
        cart,
        setCart,
        addItemToCart,
        updateItemInCart,
        removeItemFromCart,
        clearCart,
        resetShipping,
        coupon,
        setCoupon,
        maxInstallments,
        setMaxInstallments,
        selectedShipping,
        setSelectedShipping,
        shippingOptions,
        setShippingOptions,
        lastCalculatedZip,
        setLastCalculatedZip,
        cartHash,
        userFilters,
        setUserFilters,
        itemsCount,
        subtotal,
        discountValue,
        shippingValue,
        total,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};